import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiUrl, getAuthHeaders } from '../../config/api.config';

const initialState = {
  subscribers: [],
  newsletters: [],
  loading: false,
  error: null,
  success: false,
  subscribeSuccess: false
};

// Subscribe (Public)
export const subscribe = createAsyncThunk(
  'subscribers/subscribe',
  async (email, { rejectWithValue }) => {
    try {
      const response = await axios.post(getApiUrl('/subscribers/subscribe'), { email });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to subscribe');
    }
  }
);

// Unsubscribe (Public)
export const unsubscribe = createAsyncThunk(
  'subscribers/unsubscribe',
  async (email, { rejectWithValue }) => {
    try {
      const response = await axios.post(getApiUrl('/subscribers/unsubscribe'), { email });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to unsubscribe');
    }
  }
);

// Get all subscribers (Admin)
export const getAllSubscribers = createAsyncThunk(
  'subscribers/getAll',
  async ({ status } = {}, { rejectWithValue }) => {
    try {
      let url = '/subscribers';
      if (status) url += `?status=${status}`;
      const response = await axios.get(getApiUrl(url), { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch subscribers');
    }
  }
);

// Delete subscriber (Admin)
export const deleteSubscriber = createAsyncThunk(
  'subscribers/delete',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(getApiUrl(`/subscribers/${id}`), { headers: getAuthHeaders() });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete subscriber');
    }
  }
);

// Send newsletter (Admin)
export const sendNewsletter = createAsyncThunk(
  'subscribers/sendNewsletter',
  async (newsletterData, { rejectWithValue }) => {
    try {
      const response = await axios.post(getApiUrl('/subscribers/newsletter'), newsletterData, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send newsletter');
    }
  }
);

// Get newsletter history (Admin)
export const getNewsletterHistory = createAsyncThunk(
  'subscribers/getNewsletterHistory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(getApiUrl('/subscribers/newsletter/history'), { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch newsletter history');
    }
  }
);

const subscriberSlice = createSlice({
  name: 'subscribers',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    clearSuccess: (state) => { state.success = false; },
    clearSubscribeSuccess: (state) => { state.subscribeSuccess = false; }
  },
  extraReducers: (builder) => {
    builder
      .addCase(subscribe.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(subscribe.fulfilled, (state) => { state.loading = false; state.subscribeSuccess = true; })
      .addCase(subscribe.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(unsubscribe.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(unsubscribe.fulfilled, (state) => { state.loading = false; state.success = true; })
      .addCase(unsubscribe.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(getAllSubscribers.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(getAllSubscribers.fulfilled, (state, action) => { state.loading = false; state.subscribers = action.payload.data.subscribers; })
      .addCase(getAllSubscribers.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(deleteSubscriber.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(deleteSubscriber.fulfilled, (state, action) => {
        state.loading = false;
        state.subscribers = state.subscribers.filter(s => s._id !== action.payload);
        state.success = true;
      })
      .addCase(deleteSubscriber.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(sendNewsletter.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(sendNewsletter.fulfilled, (state) => { state.loading = false; state.success = true; })
      .addCase(sendNewsletter.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(getNewsletterHistory.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(getNewsletterHistory.fulfilled, (state, action) => { state.loading = false; state.newsletters = action.payload.data.newsletters; })
      .addCase(getNewsletterHistory.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  }
});

export const { clearError, clearSuccess, clearSubscribeSuccess } = subscriberSlice.actions;
export default subscriberSlice.reducer;
